import { useState, useCallback, useRef } from 'react';
import { Client } from '@microsoft/microsoft-graph-client';
import {
  M365User,
  OffboardingAction,
  OffboardingReport,
  GroupMembership,
  UserLicense,
} from '../types';
import * as graphService from '../services/graphService';
import * as exchangeService from '../services/exchangeService';

/**
 * Hook principal pour la logique d'offboarding
 * Gère la checklist, l'exécution séquentielle et le rapport
 */

/**
 * Liste des actions d'offboarding, dans leur ordre d'exécution.
 *
 * L'ordre n'est pas cosmétique, il évite deux dégâts irréversibles :
 *  - le retrait des groupes vient APRÈS les opérations Exchange, car un groupe peut
 *    porter la licence : le retirer trop tôt place la boîte en sursis de 30 jours ;
 *  - le retrait des licences vient APRÈS la conversion en boîte partagée, pour la
 *    même raison — une boîte utilisateur délicenciée est supprimée au bout de 30 jours,
 *    une boîte partagée de moins de 50 Go n'a pas besoin de licence.
 *
 * La désactivation du compte est décochée par défaut et placée APRÈS les opérations
 * Exchange : ainsi, si la chaîne Exchange échoue, le compte reste intact et l'admin
 * peut reprendre sans rien débloquer. Elle n'empêche pas la délégation — le délégué
 * s'authentifie avec ses propres identifiants — mais Microsoft la recommande pour un
 * départ, car après conversion en boîte partagée « the original username and password
 * will continue to work on the shared mailbox ». C'est le reset du mot de passe, coché
 * par défaut, qui ferme cette porte-là.
 */
export function getDefaultActions(): OffboardingAction[] {
  return [
    {
      id: 'revokeSessions',
      label: 'Révoquer les sessions',
      description: 'Révoque toutes les sessions actives (tokens, cookies)',
      checked: true,
      status: 'pending',
    },
    {
      id: 'resetPassword',
      label: 'Reset du mot de passe',
      description: 'Réinitialise le mot de passe avec un mot de passe aléatoire fort',
      checked: true,
      status: 'pending',
    },
    {
      id: 'hideFromGAL',
      label: 'Masquer de la GAL',
      description: 'Cache l\'utilisateur de la liste d\'adresses globale',
      checked: true,
      status: 'pending',
    },
    {
      id: 'convertSharedMailbox',
      label: 'Convertir en SharedMailbox',
      description: 'Convertit la boîte mail en boîte partagée, avant tout retrait de licence',
      checked: true,
      status: 'pending',
    },
    {
      id: 'setAutoReply',
      label: 'Message d\'absence',
      description: 'Configure un message d\'absence automatique permanent',
      checked: false,
      status: 'pending',
      requiresPopup: true,
      popupData: {},
    },
    {
      id: 'delegateMailbox',
      label: 'Déléguer la boîte mail',
      description: 'Donne un accès complet à la boîte mail à un autre utilisateur',
      checked: false,
      status: 'pending',
      requiresPopup: true,
      popupData: {},
    },
    {
      id: 'forwardMail',
      label: 'Transférer les mails',
      description: 'Redirige les mails entrants vers une autre adresse',
      checked: false,
      status: 'pending',
      requiresPopup: true,
      popupData: {},
    },
    {
      id: 'disableAccount',
      label: 'Désactiver le compte',
      description:
        'Bloque la connexion. N\'empêche PAS la délégation : le délégué se connecte avec ses propres identifiants',
      checked: false,
      status: 'pending',
    },
    {
      id: 'removeGroups',
      label: 'Retirer de tous les groupes',
      description: 'Retire l\'utilisateur de ses groupes Entra ID modifiables',
      checked: true,
      status: 'pending',
    },
    {
      id: 'removeLicenses',
      label: 'Retirer les licences',
      description: 'Libère les licences attribuées directement (après conversion en boîte partagée)',
      checked: true,
      status: 'pending',
    },
    {
      id: 'clearCompany',
      label: 'Supprimer le champ Company',
      description: 'Efface le champ "Company" du profil utilisateur',
      checked: true,
      status: 'pending',
    },
  ];
}

export function useOffboarding() {
  const [actions, setActions] = useState<OffboardingAction[]>(getDefaultActions());
  const [isExecuting, setIsExecuting] = useState(false);
  const [currentActionIndex, setCurrentActionIndex] = useState(-1);
  const [report, setReport] = useState<OffboardingReport | null>(null);
  const abortRef = useRef(false);

  /** Toggle une action dans la checklist */
  const toggleAction = useCallback((actionId: string) => {
    setActions((prev) =>
      prev.map((a) => (a.id === actionId ? { ...a, checked: !a.checked } : a))
    );
  }, []);

  /** Met à jour les données popup d'une action */
  const updatePopupData = useCallback((actionId: string, data: Record<string, string>) => {
    setActions((prev) =>
      prev.map((a) => (a.id === actionId ? { ...a, popupData: { ...a.popupData, ...data } } : a))
    );
  }, []);

  /**
   * Demande l'arrêt après l'action en cours.
   * On n'interrompt jamais un appel déjà parti : une conversion de boîte à moitié
   * lancée serait pire qu'une conversion terminée.
   */
  const cancelExecution = useCallback(() => {
    abortRef.current = true;
  }, []);

  /** Exécute toutes les actions cochées séquentiellement */
  const executeOffboarding = useCallback(
    async (
      client: Client,
      functionToken: string,
      targetUser: M365User,
      groups: GroupMembership[],
      licenses: UserLicense[],
      executedBy: string
    ) => {
      setIsExecuting(true);
      abortRef.current = false;
      const updatedActions = [...actions];
      const mailboxIdentity = targetUser.mail || targetUser.userPrincipalName;

      // Garde-fou appris d'un passage réel : la conversion en boîte partagée avait
      // échoué, mais la séquence avait continué et retiré la licence. Une boîte
      // UTILISATEUR sans licence est supprimée au bout de 30 jours. Si la conversion
      // était demandée et n'a pas abouti, on n'exécute plus rien qui retire la licence.
      let sharedMailboxFailed = false;
      const dependsOnSharedMailbox = new Set(['removeGroups', 'removeLicenses']);

      for (let i = 0; i < updatedActions.length; i++) {
        const action = updatedActions[i];

        // Arrêt demandé : le reste est marqué ignoré plutôt que laissé en attente,
        // pour que le rapport dise clairement ce qui n'a pas été fait.
        if (abortRef.current) {
          updatedActions[i] = {
            ...action,
            status: 'skipped',
            infoMessage: action.checked ? 'Interrompu par l\'administrateur' : undefined,
          };
          setActions([...updatedActions]);
          continue;
        }

        if (!action.checked) {
          updatedActions[i] = { ...action, status: 'skipped' };
          setActions([...updatedActions]);
          continue;
        }

        // La conversion en boîte partagée a échoué : tout ce qui ferait perdre la
        // licence est bloqué, sinon la boîte disparaît dans 30 jours.
        if (sharedMailboxFailed && dependsOnSharedMailbox.has(action.id)) {
          updatedActions[i] = {
            ...action,
            status: 'skipped',
            infoMessage:
              'Bloqué : la conversion en boîte partagée a échoué. Retirer la licence maintenant '
              + 'supprimerait la boîte dans 30 jours. Corriger la conversion, puis relancer.',
          };
          setActions([...updatedActions]);
          continue;
        }

        // Marquer l'action en cours
        updatedActions[i] = { ...action, status: 'running' };
        setActions([...updatedActions]);
        setCurrentActionIndex(i);

        try {
          let infoMessage: string | undefined;

          switch (action.id) {
            case 'revokeSessions':
              await graphService.revokeSignInSessions(client, targetUser.id);
              break;

            case 'resetPassword':
              await graphService.resetPassword(client, targetUser.id, targetUser.onPremisesSyncEnabled);
              break;

            case 'disableAccount':
              await graphService.disableAccount(client, targetUser.id);
              break;

            case 'hideFromGAL':
              await exchangeService.hideFromGAL(mailboxIdentity, functionToken);
              break;

            case 'convertSharedMailbox':
              await exchangeService.convertToSharedMailbox(mailboxIdentity, functionToken);
              break;

            case 'setAutoReply':
              if (!action.popupData?.message) {
                throw new Error('Message d\'absence non renseigné');
              }
              await exchangeService.setAutoReply(
                mailboxIdentity,
                action.popupData.message,
                functionToken
              );
              break;

            case 'delegateMailbox':
              if (!action.popupData?.delegateEmail) {
                throw new Error('Email du délégué non renseigné');
              }
              await exchangeService.delegateMailbox(
                mailboxIdentity,
                action.popupData.delegateEmail,
                functionToken
              );
              break;

            case 'forwardMail':
              if (!action.popupData?.forwardTo) {
                throw new Error('Email de destination non renseigné');
              }
              await exchangeService.setMailForwarding(
                mailboxIdentity,
                action.popupData.forwardTo,
                functionToken
              );
              break;

            case 'removeGroups': {
              const result = await graphService.removeFromAllGroups(client, targetUser.id, groups);
              if (result.errors.length > 0) {
                throw new Error(
                  `Retirés : ${result.removed.length}. Échecs : ${result.errors.join(' | ')}`
                );
              }
              if (result.skipped.length > 0) {
                infoMessage =
                  `${result.removed.length} retiré(s). ${result.skipped.length} hors de portée de Graph : `
                  + `${result.skipped.join(', ')}. Les groupes dynamiques se vident d'eux-mêmes `
                  + `quand les attributs du compte ne correspondent plus à leur règle.`;
              } else {
                infoMessage = `${result.removed.length} groupe(s) retiré(s)`;
              }
              break;
            }

            case 'removeLicenses': {
              const result = await graphService.removeDirectLicenses(client, targetUser.id, licenses);
              if (!result.removed.length && !result.keptFromGroups.length) {
                infoMessage = 'Aucune licence assignée';
              } else {
                const parts: string[] = [];
                if (result.removed.length) parts.push(`Libérées : ${result.removed.join(', ')}`);
                if (result.keptFromGroups.length) {
                  parts.push(
                    `Héritées d'un groupe, retirées avec l'appartenance : ${result.keptFromGroups.join(', ')}`
                  );
                }
                infoMessage = parts.join(' — ');
              }
              break;
            }

            case 'clearCompany':
              await graphService.clearCompanyName(client, targetUser.id);
              break;
          }

          updatedActions[i] = { ...action, status: 'success', infoMessage };
        } catch (error: any) {
          if (action.id === 'convertSharedMailbox') {
            sharedMailboxFailed = true;
          }
          // Extraire le message Graph précis si disponible
          const graphMessage =
            error?.body
              ? (() => { try { return JSON.parse(error.body)?.error?.message; } catch { return null; } })()
              : error?.message;
          updatedActions[i] = {
            ...action,
            status: 'error',
            errorMessage: graphMessage || error.message || 'Erreur inconnue',
          };
        }

        setActions([...updatedActions]);
      }

      // Générer le rapport
      const finalReport: OffboardingReport = {
        targetUser,
        executedBy,
        executedAt: new Date().toISOString(),
        actions: updatedActions,
      };
      setReport(finalReport);
      setIsExecuting(false);
      setCurrentActionIndex(-1);
      abortRef.current = false;

      return finalReport;
    },
    [actions]
  );

  /** Remet tout à zéro */
  const reset = useCallback(() => {
    setActions(getDefaultActions());
    setIsExecuting(false);
    setCurrentActionIndex(-1);
    setReport(null);
    abortRef.current = false;
  }, []);

  return {
    actions,
    isExecuting,
    currentActionIndex,
    report,
    toggleAction,
    updatePopupData,
    executeOffboarding,
    cancelExecution,
    reset,
  };
}
